// Connected accounts on "Your account" (spec/07 §2 "Account linking", §3.4; F-IAM-03 … F-IAM-05): a staff member
// links Google, Discord or Steam to their own account, or unlinks one, each listed only while this deployment has it
// switched on (`GET /v1/meta`'s `signIn.social`). The organisation console only: the HQ console is staff-only and
// invitation-first, and offers no social sign-in at all.
//
//   Linking: from this signed-in session only (`/v1/auth/link-social` for Google and Discord, `/v1/auth/steam/start`
//   with `link: true` for Steam), every URL on this console's own origin; the provider sends the browser back to this
//   page with `?linked=<provider>`, or with `?error=<code>` and the `provider` we named.
//
//   Unlinking: confirmed in a centred dialog. The server refuses the last way in it counts (the password and every
//   linked provider, `FAILED_TO_UNLINK_LAST_ACCOUNT`) and the password itself, and asks for a session signed in
//   within ten minutes (`SESSION_NOT_FRESH`). The page counts the same way first and says why it will not offer it.
//
//   A row shows the provider and when it was linked. The api keeps no provider address or name for a linked account,
//   so none is shown, and the account's own email is never put beside a provider it did not come from.

import type { LinkedAccount, SocialProvider } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, ProviderTile, Skeleton, useToast } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode } from '../api/problems';
import { here, providerNavigation, useSocialProviders } from '../auth/social';
import { useProblem } from '../api/use-problem';
import { LoadFailure } from '../shell/LoadFailure';
import { useSignOut } from '../shell/AppShell';
import { useSurface } from '../surface';
import { Section } from '../ui';
import { accountKeys, useLinkedAccounts } from './queries';

const isProvider = (value: unknown): value is SocialProvider => value === 'google' || value === 'discord' || value === 'steam';

type LinkRefusal = 'cancelled' | 'expired' | 'takenByOther' | 'unverified' | 'generic';

/** What a refused link's `?error=` means, as the copy key under `account.connected.error`. */
function linkRefusal(code: string): LinkRefusal {
  switch (code) {
    case 'access_denied':
      return 'cancelled';
    case 'state_mismatch':
    case 'state_not_found':
    case 'state_security_mismatch':
    case 'please_restart_the_process':
      return 'expired';
    case 'account_already_linked_to_different_user':
      return 'takenByOther';
    case 'unable_to_link_account':
    case 'email_not_verified':
      return 'unverified';
    default:
      return 'generic';
  }
}

type UnlinkFailure = 'stale' | 'lastWay' | 'unlinkFailed';

function unlinkFailureOf(error: unknown): UnlinkFailure {
  switch (problemCode(error)) {
    case 'SESSION_NOT_FRESH':
      return 'stale';
    case 'FAILED_TO_UNLINK_LAST_ACCOUNT':
      return 'lastWay';
    default:
      return 'unlinkFailed';
  }
}

/** `path` with `params` added to its query. */
function withQuery(path: string, params: Readonly<Record<string, string>>): string {
  return `${path}?${new URLSearchParams(params).toString()}`;
}

export function ConnectedAccountsSection(): ReactNode {
  const surface = useSurface();
  const providers = useSocialProviders();
  if (surface.id !== 'org' || providers.length === 0) return null;
  return <ConnectedAccounts providers={providers} />;
}

function ConnectedAccounts({ providers }: { readonly providers: readonly SocialProvider[] }): ReactNode {
  const { t } = useTranslation('console');
  const accounts = useLinkedAccounts();
  const location = useLocation();
  const router = useRouter();
  const { show } = useToast();
  const search = location.search as Record<string, unknown>;
  const [returned] = useState(() => ({
    linked: typeof search['linked'] === 'string' ? search['linked'] : undefined,
    error: typeof search['error'] === 'string' ? search['error'] : undefined,
    provider: typeof search['provider'] === 'string' ? search['provider'] : undefined,
  }));
  const handled = useRef(false);

  // Back from a provider: say what happened once, then take the codes out of the address bar.
  useEffect(() => {
    if (handled.current || (returned.linked === undefined && returned.error === undefined)) return;
    handled.current = true;
    if (isProvider(returned.linked) && returned.error === undefined) {
      show({ tone: 'ok', title: t('account.connected.linkedToast', { provider: t(`account.connected.provider.${returned.linked}`) }) });
    }
    router.history.replace(location.pathname);
  }, [returned, router, location.pathname, show, t]);

  const refusal = returned.error === undefined ? undefined : isProvider(returned.provider) ? linkRefusal(returned.error) : 'generic';
  const refusedName = isProvider(returned.provider) ? t(`account.connected.provider.${returned.provider}`) : t('account.connected.yourAccount');

  return (
    <Section title={t('account.connected.title')} description={t('account.connected.hint')} body="rows">
      {refusal === undefined ? null : (
        <div className="console-account__pad">
          <p className="console-alert" role="alert">
            {t(`account.connected.error.${refusal}`, { provider: refusedName })}
          </p>
        </div>
      )}
      {accounts.data === undefined ? (
        <div className="console-account__pad">
          {accounts.isError ? (
            <LoadFailure error={accounts.error} message={t('account.connected.unavailable')} onRetry={() => void accounts.refetch()} />
          ) : (
            <Skeleton shape="text" lines={3} />
          )}
        </div>
      ) : (
        <ul className="console-account__rows" aria-label={t('account.connected.listLabel')}>
          {providers.map((provider) => (
            <ConnectedRow
              key={provider}
              provider={provider}
              account={accounts.data.find((one) => one.providerId === provider)}
              // better-auth's own count (`allowUnlinkingAll: false`): the password and every linked provider.
              otherWaysIn={accounts.data.length > 1}
              returnTo={location.pathname}
            />
          ))}
        </ul>
      )}
    </Section>
  );
}

function ConnectedRow({
  provider,
  account,
  otherWaysIn,
  returnTo,
}: {
  readonly provider: SocialProvider;
  readonly account: LinkedAccount | undefined;
  readonly otherWaysIn: boolean;
  /** This page's path: where the provider sends the browser back. */
  readonly returnTo: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const api = useApi();
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  const { show } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [linking, setLinking] = useState(false);
  const [failure, setFailure] = useState<'linkFailed' | UnlinkFailure | null>(null);
  // The server's own reason, when it gave one the catalogue has words for (a rate limit, a lapsed session); else the screen's line.
  const [reason, setReason] = useState<string | null>(null);
  const problems = useProblem();
  const name = t(`account.connected.provider.${provider}`);

  const unlink = useMutation({
    mutationFn: (accountId: string) => api.unlinkAccount({ body: { accountId } }),
    onSuccess: async () => {
      setConfirming(false);
      setFailure(null);
      show({ tone: 'ok', title: t('account.connected.unlinkedToast', { provider: name }) });
      await queryClient.invalidateQueries({ queryKey: accountKeys.linkedAccounts });
    },
    onError: (error) => {
      setConfirming(false);
      const kind = unlinkFailureOf(error);
      setFailure(kind);
      setReason(kind === 'unlinkFailed' ? problems.text(error, { fallback: t('account.connected.unlinkFailed', { provider: name }) }) : null);
    },
  });

  const link = async () => {
    setFailure(null);
    setReason(null);
    setLinking(true);
    const callbackURL = here(withQuery(returnTo, { linked: provider }));
    const errorCallbackURL = here(withQuery(returnTo, { provider }));
    try {
      const url =
        provider === 'steam'
          ? (await api.steamStart({ body: { callbackURL, errorCallbackURL, link: true } })).url
          : (await api.linkSocialAccount({ body: { provider, callbackURL, errorCallbackURL, disableRedirect: true } })).url;
      providerNavigation.assign(url);
    } catch (error) {
      setLinking(false);
      setFailure('linkFailed');
      setReason(problems.text(error, { fallback: t('account.connected.linkFailed', { provider: name }) }));
    }
  };

  return (
    <li className="console-account__row console-connected__row">
      <ProviderTile provider={provider} />
      <span className="console-account__row-main console-connected__main">
        <span className="console-account__row-title">
          {name}
          <Badge tone={account === undefined ? 'neutral' : 'ok'} dot>
            {account === undefined ? t('account.connected.notLinked') : t('account.connected.linked')}
          </Badge>
        </span>
        {account === undefined ? null : (
          <span className="console-subtle">{t('account.connected.linkedOn', { date: format.date(account.createdAt, 'medium') })}</span>
        )}
        {account !== undefined && !otherWaysIn ? <span className="console-subtle">{t('account.connected.onlyWay')}</span> : null}
        {failure === null ? null : (
          <span className="console-connected__failure" role="alert">
            <span className="console-alert">{reason ?? t(`account.connected.${failure}`, { provider: name })}</span>
            {failure === 'stale' ? (
              <span>
                <Button variant="secondary" size="sm" onClick={() => void signOut()}>
                  {t('account.connected.signInAgain')}
                </Button>
              </span>
            ) : null}
          </span>
        )}
      </span>
      {account === undefined ? (
        <Button variant="secondary" size="sm" busy={linking} onClick={() => void link()}>
          {t('account.connected.link', { provider: name })}
        </Button>
      ) : (
        <Dialog
          title={t('account.connected.confirmTitle', { provider: name })}
          description={t('account.connected.confirmBody', { provider: name })}
          open={confirming}
          onOpenChange={setConfirming}
          trigger={
            <Button variant="ghost" size="sm" disabled={!otherWaysIn} aria-label={t('account.connected.unlinkLabel', { provider: name })}>
              {t('account.connected.unlink')}
            </Button>
          }
          footer={
            <>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                {t('account.connected.cancel')}
              </Button>
              <Button variant="danger" className="clav-button--danger-solid" busy={unlink.isPending} onClick={() => unlink.mutate(account.id)}>
                {t('account.connected.confirm', { provider: name })}
              </Button>
            </>
          }
        />
      )}
    </li>
  );
}
